import { useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addDays,
  addWeeks,
  format,
  formatISO,
  isSameDay,
  isToday as isTodayFn,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  startOfWeek,
} from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { api, type Article, type ArticleStatus } from '@/lib/api'
import { cn } from '@/lib/utils'

const STATUS_CLASS: Record<ArticleStatus, string> = {
  draft:     'bg-status-draft-bg border-l-status-draft-accent text-status-draft-fg',
  in_review: 'bg-status-review-bg border-l-status-review-accent text-status-review-fg',
  approved:  'bg-status-approved-bg border-l-status-approved-accent text-status-approved-fg',
  published: 'bg-status-published-bg border-l-status-published-accent text-status-published-fg',
  archived:  'bg-surface-muted border-l-ink-tertiary text-ink-secondary',
}

const STATUS_LABEL: Record<ArticleStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
}

function ArticleCard({ article, dragging }: { article: Article; dragging?: boolean }) {
  return (
    <div
      className={cn(
        'rounded border-l-2 px-2 py-1.5 text-[11px] leading-tight select-none',
        STATUS_CLASS[article.status],
        dragging ? 'shadow-md opacity-90' : 'cursor-grab active:cursor-grabbing hover:opacity-90',
      )}
      title={article.title}
    >
      <div className="font-medium truncate">{article.title}</div>
      <div className="opacity-80 mt-0.5 flex justify-between gap-1">
        <span className="truncate">
          {article.word_count ? `${(article.word_count / 1000).toFixed(1)}k` : article.type}
        </span>
        <span className="truncate opacity-70">{STATUS_LABEL[article.status]}</span>
      </div>
    </div>
  )
}

function DraggableArticle({ article }: { article: Article }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: article.id,
    data: { article },
  })
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn('outline-none', isDragging && 'opacity-40')}
    >
      <ArticleCard article={article} />
    </div>
  )
}

function DayColumn({
  date,
  articles,
  isToday,
}: {
  date: Date
  articles: Article[]
  isToday: boolean
}) {
  const dayKey = formatISO(date, { representation: 'date' })
  const { setNodeRef, isOver } = useDroppable({ id: `day:${dayKey}`, data: { date } })
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col gap-1.5 p-1.5 rounded-md min-h-[160px] transition-colors',
        isOver ? 'bg-brand-50 ring-1 ring-brand-400' : 'bg-transparent',
      )}
    >
      <div
        className={cn(
          'text-xs px-0.5 pb-1 border-b border-border flex justify-between items-baseline',
          isToday ? 'text-brand-600 font-medium' : 'text-ink-tertiary',
        )}
      >
        <span>{format(date, 'EEE')}</span>
        <span className={cn('text-sm', isToday && 'font-medium')}>{format(date, 'd')}</span>
      </div>
      {articles.length === 0 ? (
        <div className="text-[11px] text-ink-tertiary/60 px-1 py-2 italic">Drop here</div>
      ) : (
        articles.map((a) => <DraggableArticle key={a.id} article={a} />)
      )}
    </div>
  )
}

function setTimePreservingClock(target: Date, source: string | null): string {
  const base = source ? new Date(source) : null
  const h = base ? base.getHours() : 9
  const m = base ? base.getMinutes() : 0
  const d = setMilliseconds(setSeconds(setMinutes(setHours(target, h), m), 0), 0)
  return d.toISOString()
}

export function CalendarPage() {
  const [weekOffset, setWeekOffset] = useState(0)
  const [activeArticle, setActiveArticle] = useState<Article | null>(null)
  const qc = useQueryClient()

  const weekStart = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 })
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const from = formatISO(days[0])
  const to = formatISO(addDays(days[6], 1))

  const articlesQK = ['articles', { from, to }] as const
  const { data: articles = [] } = useQuery({
    queryKey: articlesQK,
    queryFn: () => api.articles({ from, to }),
  })

  const reschedule = useMutation({
    mutationFn: ({ id, scheduled_for }: { id: string; scheduled_for: string }) =>
      api.updateArticle(id, { scheduled_for }),
    onMutate: async ({ id, scheduled_for }) => {
      await qc.cancelQueries({ queryKey: articlesQK })
      const prev = qc.getQueryData<Article[]>(articlesQK)
      qc.setQueryData<Article[]>(articlesQK, (old = []) =>
        old.map((a) => (a.id === id ? { ...a, scheduled_for } : a)),
      )
      return { prev }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(articlesQK, ctx.prev)
      toast.error(`Reschedule failed: ${(err as Error).message}`)
    },
    onSuccess: (updated) => {
      toast.success(`Moved to ${format(new Date(updated.scheduled_for!), 'EEE, MMM d')}`)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: articlesQK }),
  })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  function handleDragStart(e: DragStartEvent) {
    const a = e.active.data.current?.article as Article | undefined
    if (a) setActiveArticle(a)
  }

  function handleDragEnd(e: DragEndEvent) {
    setActiveArticle(null)
    const { active, over } = e
    if (!over) return
    const target = over.data.current?.date as Date | undefined
    const article = active.data.current?.article as Article | undefined
    if (!target || !article) return
    if (article.scheduled_for && isSameDay(new Date(article.scheduled_for), target)) return
    const next = setTimePreservingClock(target, article.scheduled_for)
    reschedule.mutate({ id: article.id, scheduled_for: next })
  }

  const weekLabel =
    format(weekStart, 'MMM d') +
    ' – ' +
    format(days[6], weekStart.getMonth() === days[6].getMonth() ? 'd, yyyy' : 'MMM d, yyyy')

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
        <div>
          <h1 className="text-xl font-medium">Editorial calendar</h1>
          <p className="text-xs text-ink-secondary">
            Drag a card to another day to reschedule. Time of day is preserved.
          </p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setWeekOffset((w) => w - 1)}
            className="p-1.5 border border-border rounded-md hover:bg-surface-muted"
            aria-label="Previous week"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className={cn(
              'text-xs px-2.5 py-1.5 border border-border rounded-md hover:bg-surface-muted',
              weekOffset === 0 && 'bg-surface-muted',
            )}
          >
            This week
          </button>
          <button
            onClick={() => setWeekOffset((w) => w + 1)}
            className="p-1.5 border border-border rounded-md hover:bg-surface-muted"
            aria-label="Next week"
          >
            <ChevronRight size={16} />
          </button>
          <span className="text-xs text-ink-tertiary ml-2">{weekLabel}</span>
        </div>
      </header>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <section className="border border-border rounded-md p-3 bg-surface-card">
          <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
          <div className="grid grid-cols-7 gap-1.5 min-w-[840px] md:min-w-0">
            {days.map((d) => {
              const dayArticles = articles.filter(
                (a) => a.scheduled_for && isSameDay(new Date(a.scheduled_for), d),
              )
              return (
                <DayColumn
                  key={d.toISOString()}
                  date={d}
                  articles={dayArticles}
                  isToday={isTodayFn(d)}
                />
              )
            })}
          </div>
          </div>
          <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 mt-3 pt-3 border-t border-border text-xs text-ink-secondary">
            {(
              [
                ['Draft', '#7F77DD'],
                ['In review', '#EF9F27'],
                ['Approved', '#1D9E75'],
                ['Published', '#888780'],
              ] as const
            ).map(([label, color]) => (
              <span key={label} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
                {label}
              </span>
            ))}
            <span className="ml-auto">{articles.length} scheduled</span>
          </div>
        </section>
        <DragOverlay dropAnimation={null}>
          {activeArticle ? <ArticleCard article={activeArticle} dragging /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}
